<template>
  <el-dialog
    v-model="visible"
    :width="options.width"
    :close-on-click-modal="false"
    :close-on-press-escape="true"
    :show-close="false"
    align-center
    append-to-body
    class="tutor-confirm-dialog"
    @closed="onClosed"
  >
    <button type="button" class="tutor-confirm-close" aria-label="close" @click="cancel">
      <el-icon :size="20"><Close /></el-icon>
    </button>

    <div class="tutor-confirm-icon">
      <template v-if="options.icon?.default !== false">
        <span class="warn-circle">!</span>
      </template>
      <el-icon v-else-if="alertType === 'error'" class="type-icon is-error" :size="40">
        <CircleCloseFilled />
      </el-icon>
      <el-icon v-else-if="alertType === 'success'" class="type-icon is-success" :size="40">
        <SuccessFilled />
      </el-icon>
      <span v-else class="warn-circle">!</span>
    </div>

    <!-- Vue2 confirmDialog: single v-html block (centered for error/success) -->
    <div
      v-if="message"
      class="tutor-confirm-message"
      :class="isCentered ? 'is-center' : 'is-left'"
      v-html="message"
    />

    <div class="tutor-confirm-footer">
      <button
        v-if="options.cancelBtn?.display !== false"
        type="button"
        class="tutor-confirm-btn is-cancel"
        :disabled="submitting"
        @click="cancel"
      >
        {{ options.cancelBtn?.text || "Cancel" }}
      </button>
      <button
        v-if="options.confirmBtn?.display !== false"
        type="button"
        class="tutor-confirm-btn is-confirm"
        :class="{ 'is-warn': useWarnConfirm }"
        :disabled="submitting"
        @click="agree"
      >
        {{ submitting ? "Submitting..." : options.confirmBtn?.text || "Confirm" }}
      </button>
    </div>
  </el-dialog>
</template>

<script setup>
import { computed, reactive, ref } from "vue";
import { Close, CircleCloseFilled, SuccessFilled } from "@element-plus/icons-vue";

/**
 * Legacy Tutor.Web confirmDialog parity (centered icon + Cancel/Confirm pills).
 * open(message, alertType, options) → Promise
 */
const visible = ref(false);
const message = ref("");
const alertType = ref("error");
const submitting = ref(false);
const options = reactive({
  cancelBtn: { display: true, text: "Cancel", color: "#E0E0E0" },
  confirmBtn: { display: true, text: "Confirm", color: "#3e67fb" },
  icon: { default: true },
  color: "white",
  width: 420,
  awaitSubmit: false,
});

let resolveFn = null;
let rejectFn = null;

const isCentered = computed(
  () =>
    alertType.value === "error" ||
    alertType.value === "success" ||
    alertType.value === "default" ||
    !alertType.value
);

const useWarnConfirm = computed(
  () =>
    options.icon?.default ||
    alertType.value === "error" ||
    alertType.value === "default" ||
    !alertType.value
);

function resetOptions(opts = {}) {
  options.cancelBtn = {
    display: true,
    text: "Cancel",
    color: "#E0E0E0",
    ...(opts.cancelBtn || {}),
  };
  options.confirmBtn = {
    display: true,
    text: "Confirm",
    color: "#3e67fb",
    ...(opts.confirmBtn || {}),
  };
  options.icon = { default: true, ...(opts.icon || {}) };
  options.color = opts.color || "white";
  options.width = opts.width || 420;
  options.awaitSubmit = !!opts.awaitSubmit;
}

function open(msg, type, opts) {
  if (visible.value) {
    console.error("TutorConfirmDialog: only one dialog at a time");
    return Promise.resolve(false);
  }
  message.value = msg || "";
  alertType.value = type || "error";
  resetOptions(opts || {});
  submitting.value = false;
  visible.value = true;
  return new Promise((resolve, reject) => {
    resolveFn = resolve;
    rejectFn = reject;
  });
}

function agree() {
  if (options.awaitSubmit) {
    submitting.value = true;
    resolveFn?.({
      result: true,
      submitcomplete: () => {
        submitting.value = false;
        visible.value = false;
        options.awaitSubmit = false;
      },
    });
    return;
  }
  resolveFn?.(true);
  visible.value = false;
}

function cancel() {
  if (options.awaitSubmit) {
    resolveFn?.({ result: false });
    options.awaitSubmit = false;
  } else {
    resolveFn?.(false);
  }
  visible.value = false;
}

function onClosed() {
  submitting.value = false;
}

defineExpose({ open, agree, cancel });
</script>

<style scoped lang="scss">
.tutor-confirm-close {
  position: absolute;
  top: 10px;
  right: 12px;
  z-index: 2;
  appearance: none;
  border: 0;
  background: transparent;
  color: #e0e0e0;
  cursor: pointer;
  padding: 4px;
  line-height: 1;

  &:hover {
    color: #bdbdbd;
  }
}

.tutor-confirm-icon {
  text-align: center;
  margin: 8px 0 4px;
}

.warn-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #ff9991;
  color: #fff;
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
}

.type-icon.is-error {
  color: #ff9991;
}
.type-icon.is-success {
  color: #3e67fb;
}

.tutor-confirm-message {
  padding: 8px 28px 4px;
  word-break: break-word;
  color: #32397a;
  font-size: 16px;
  line-height: 1.5;

  &.is-center {
    text-align: center;
  }
  &.is-left {
    text-align: left;
  }
}

.tutor-confirm-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 16px 0 20px;
}

.tutor-confirm-btn {
  appearance: none;
  border: 0;
  min-width: 88px;
  height: 28px;
  padding: 0 18px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  cursor: pointer;
  line-height: 28px;

  &.is-cancel {
    background: #e0e0e0;
  }

  &.is-confirm {
    background: #3e67fb;
  }

  &.is-confirm.is-warn {
    background: #ff9991 !important;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
}
</style>

<style lang="scss">
/* el-dialog shell — teleported */
.tutor-confirm-dialog.el-dialog {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 28px rgba(50, 57, 122, 0.18);
}

.tutor-confirm-dialog .el-dialog__header {
  display: none;
}

.tutor-confirm-dialog .el-dialog__body {
  padding: 8px 0 0;
  position: relative;
}
</style>
